import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Group,
  LoadingOverlay,
  NumberInput,
  Select,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { IconDeviceFloppy } from "@tabler/icons";
import { CREATE_LOAN_CATEGORY } from "apollo/mutuations";
import { ADMIN_LOAN_CATEGORIES, AUTH, LOAN_PROVIDERS } from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";

const LoanCategoryAddModal = ({ setOpened, size, activePage, refetch }) => {
  const [isLoanProvider, setIsLoanProvider] = useState(false);
  const [currentProviderId, setCurrentProviderId] = useState(null);

  // Check if current user is a loan provider
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles")) || [];
    const isProvider = roles.includes("loan_provider");
    setIsLoanProvider(isProvider);
  }, []);

  // Get current user data
  const { data: authData } = useQuery(AUTH, {
    skip: !isLoanProvider,
  });

  // Fetch loan providers to match with current user
  const { data: providersData, refetch: refetchProviders } = useQuery(
    LOAN_PROVIDERS,
    {
      variables: {
        first: 1000,
        page: 1,
        search: "",
      },
      skip: !isLoanProvider,
      onCompleted(data) {
        // Match provider by user_id when available
        if (isLoanProvider && authData?.auth?.id) {
          const provider = data?.loanProviders?.data?.find(
            (p) => p.user_id === authData.auth.id,
          );
          if (provider) {
            setCurrentProviderId(provider.id);
            form.setFieldValue("provider_id", provider.id);
          }
        }
      },
    },
  );

  const form = useForm({
    initialValues: {
      name: "",
      description: "",
      icon: "",
      sort_order: 0,
      provider_id: "",
      status: "ACTIVE",
    },
    validate: {
      name: (value) => (value ? null : "Name is required"),
      sort_order: (value) =>
        value >= 0 ? null : "Sort order must be 0 or greater",
    },
  });

  useEffect(() => {
    if (!isLoanProvider) {
      refetchProviders();
    }
  }, [isLoanProvider]);

  const providerOptions =
    providersData?.loanProviders?.data?.map((provider) => ({
      value: provider.id,
      label: provider.name,
    })) || [];

  const [createLoanCategory, { loading }] = useMutation(CREATE_LOAN_CATEGORY, {
    refetchQueries: [
      {
        query: ADMIN_LOAN_CATEGORIES,
        variables: {
          first: parseInt(size),
          page: activePage,
          search: "",
          orderBy: [
            {
              column: "SORT_ORDER",
              order: "ASC",
            },
          ],
        },
      },
    ],
    onCompleted() {
      showNotification({
        color: "green",
        title: "Success",
        message: "Loan Category Created Successfully",
      });
      setOpened(false);
      form.reset();
      if (refetch) refetch();
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Failed to Create Loan Category",
      });
    },
  });

  const handleSubmit = (values) => {
    createLoanCategory({
      variables: {
        name: values.name,
        description: values.description || null,
        icon: values.icon || null,
        sort_order: values.sort_order || 0,
        provider_id: values.provider_id || null,
        status: values.status,
      },
    });
  };

  const statusOptions = [
    { value: 1, label: "Active" },
    { value: 0, label: "Inactive" },
  ];

  return (
    <div style={{ position: "relative", minHeight: "400px" }}>
      <LoadingOverlay
        visible={loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          label="Name"
          placeholder="Enter category name"
          required
          {...form.getInputProps("name")}
          mb="md"
        />

        <Textarea
          label="Description"
          placeholder="Enter description"
          minRows={3}
          {...form.getInputProps("description")}
          mb="md"
        />

        <TextInput
          label="Icon"
          placeholder="las la-store"
          {...form.getInputProps("icon")}
          mb="md"
          description="Enter Line Awesome icon class (e.g., 'las la-store')"
        />

        <NumberInput
          label="Sort Order"
          placeholder="0"
          min={0}
          {...form.getInputProps("sort_order")}
          mb="md"
        />

        {!isLoanProvider && (
          <Select
            label="Loan Provider"
            placeholder="Select provider"
            data={providerOptions}
            searchable
            clearable
            {...form.getInputProps("provider_id")}
            mb="md"
          />
        )}

        <Select
          label="Status"
          placeholder="Select status"
          data={statusOptions}
          {...form.getInputProps("status")}
          mb="md"
        />

        <Group position="right" mt="xl">
          <Button
            type="submit"
            leftIcon={<IconDeviceFloppy size={16} />}
            style={{ backgroundColor: "#FF6A00", color: "#FFFFFF" }}
            disabled={loading}
          >
            Save
          </Button>
        </Group>
      </form>
    </div>
  );
};

export default LoanCategoryAddModal;
